import { useState, useRef } from 'react';
import './SearchInput.css';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit?: (value: string) => void;
  placeholder?: string;
  suggestions?: string[];
  onSuggestionSelect?: (suggestion: string) => void;
  className?: string;
  id?: string;
  'aria-label'?: string;
}

/**
 * SEO-optimized search input component with autocomplete
 */
export function SearchInput({
  value,
  onChange,
  onSubmit,
  placeholder = 'Search peptides by name (BPC-157, Semax, LL-37…)',
  suggestions = [],
  onSuggestionSelect,
  className = '',
  id = 'peptide-search',
  'aria-label': ariaLabel = 'Search peptides',
}: SearchInputProps) {
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  // Derive showSuggestions from state instead of using effect
  const showSuggestions = isFocused && suggestions.length > 0;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSubmit) {
      onSubmit(value);
    }
    inputRef.current?.blur();
  };

  const handleSuggestionClick = (suggestion: string) => {
    onChange(suggestion);
    if (onSuggestionSelect) {
      onSuggestionSelect(suggestion);
    }
    setIsFocused(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      setIsFocused(false);
      inputRef.current?.blur();
    }
  };

  return (
    <div className={`search-input-container ${className}`}>
      <form onSubmit={handleSubmit} role="search" aria-label={ariaLabel}>
        <label htmlFor={id} className="visually-hidden">
          {ariaLabel}
        </label>
        <div className="search-input-wrapper">
          <input
            ref={inputRef}
            id={id}
            type="search"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            onFocus={() => setIsFocused(true)}
            onBlur={() => {
              // Delay to allow suggestion click
              setTimeout(() => setIsFocused(false), 200);
            }}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            aria-label={ariaLabel}
            aria-autocomplete="list"
            aria-expanded={showSuggestions}
            aria-controls={showSuggestions ? 'suggestions-list' : undefined}
            className="search-input"
            autoComplete="off"
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
          {value && (
            <button
              type="button"
              onClick={() => {
                onChange('');
                inputRef.current?.focus();
              }}
              className="search-clear"
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      </form>
      {showSuggestions && suggestions.length > 0 && (
        <div
          ref={suggestionsRef}
          id="suggestions-list"
          className="search-suggestions"
          role="listbox"
          aria-label="Search suggestions"
        >
          {suggestions.map((suggestion, index) => (
            <button
              key={`${suggestion}-${index}`}
              type="button"
              onClick={() => handleSuggestionClick(suggestion)}
              className="search-suggestion-item"
              role="option"
              aria-selected={value === suggestion}
            >
              {suggestion}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

